"use client";

import { useProfileQuery } from "@/hooks/queries/use-profile";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com";

export const POSTHOG_CONSENT_EVENT = "posthog-consent-changed";

function readAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem("cookie-preferences");
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: boolean };
    return !!parsed?.analytics;
  } catch {
    return false;
  }
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { data: profile } = useProfileQuery();
  const initialized = useRef(false);
  const identifiedUserId = useRef<string | null>(null);
  const [consentGranted, setConsentGranted] = useState(false);

  useEffect(() => {
    setConsentGranted(readAnalyticsConsent());

    const sync = () => setConsentGranted(readAnalyticsConsent());
    const onStorage = (e: StorageEvent) => {
      if (e.key === "cookie-preferences" || e.key === "cookie-consent") sync();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(POSTHOG_CONSENT_EVENT, sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(POSTHOG_CONSENT_EVENT, sync);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!POSTHOG_KEY) return;

    if (!consentGranted) {
      if (initialized.current) {
        posthog.opt_out_capturing();
        posthog.reset();
        identifiedUserId.current = null;
      }
      return;
    }

    if (!initialized.current) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        defaults: "2026-05-30",
        opt_out_capturing_by_default: true,
      });
      initialized.current = true;
    }
    posthog.opt_in_capturing();
  }, [consentGranted]);

  useEffect(() => {
    if (!initialized.current || !consentGranted) return;

    const currentUserId = profile?.user_id ?? null;

    if (currentUserId && identifiedUserId.current !== currentUserId) {
      posthog.identify(currentUserId);
      identifiedUserId.current = currentUserId;
    } else if (!currentUserId && identifiedUserId.current) {
      posthog.reset();
      identifiedUserId.current = null;
    }
  }, [consentGranted, profile?.user_id]);

  return <>{children}</>;
}
